import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    'input',
    'selectedOption',
    'option',
    'optionsContainer'
  ]
  static values = {
    url: String,
    name: String,
    formId: String,
    initialLoad: Boolean,
    cannotEmptyLoad: Boolean,
    reload: Boolean
  }

  initialize() {
    this.debounceSearch = debounce(this.search.bind(this), 300, false)
  }

  connect() {
    this.options = []
    this.typeaheadQuery = undefined
    if (this.initialLoadValue) {
      this.loadFilterValues()
    }
  }

  get form() {
    // Prefer explicit id if provided and present; otherwise, closest form
    if (this.hasFormIdValue) {
      const byId = document.getElementById(this.formIdValue)
      if (byId instanceof HTMLFormElement) return byId
    }
    return this.inputTarget?.closest('form') || this.element.closest('form') || null
  }

  selectedValues() {
    let values = []
    for (let option of this.selectedOptionTargets) {
      values.push(option.value)
    }
    return values
  }

  loadFilterValues() {
    if (this.cannotEmptyLoadValue && this.inputTarget.value === '') { return }
    if (this.options.length > 0 && this.typeaheadQuery === undefined) { return }

    let data = new FormData(this.form)
    data.delete("authenticity_token")
    data.delete(this.inputTarget.name)
    let urlParams = new URLSearchParams(data)
    urlParams.append(`filter_name`, this.nameValue)
    if (this.typeaheadQuery) {
      urlParams.append(`filter_value`, this.typeaheadQuery)
    }
    const url = `${this.urlValue}?${urlParams.toString()}`
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        this.typeaheadQuery = undefined
        this.options = json.options
        this.renderOptions(this.options)
      })
      .catch((err) => {
        this.typeaheadQuery = undefined
        this.alertMessage('Unable to load more options')
      })
  }

  renderOptions(options) {
    let selectedValues = this.selectedValues()
    this.optionsContainerTarget.innerHTML = ''
    let filteredOptions = options.filter((option) => typeof option === 'string' && option !== '' && !selectedValues.includes(option))
    if (filteredOptions.length > 0) {
      filteredOptions.forEach((option, index) => {
        let checkbox = this.buildCheckbox(option, index)
        this.optionsContainerTarget.append(checkbox)
      })
    } else if (this.inputTarget !== '') {
      this.alertMessage(`No results starting with ${this.inputTarget.value}`)
    } else {
      this.alertMessage('No results for this filter for this search')
    }
  }

  alertMessage(msg) {
    this.optionsContainerTarget.innerHTML = `<div class="px-3 py-2"><span class="text-muted semibold">${msg}</span></div>`
  }

  buildCheckbox(option, index) {
    let container = document.createElement('div')
    container.className = 'filter-list-item px-3 py-2 d-flex gap-3 align-items-center'
    if (index % 2 === 1) {
      container.classList.add('bg-color--neutral-97')
    }

    let checkbox = document.createElement('input')
    checkbox.type = 'checkbox'
    checkbox.value = option
    checkbox.id = `${this.nameValue}_option_${index}`
    checkbox.name = this.inputTarget.name
    checkbox.dataset.typeaheadFilterTarget = 'option'

    let label = document.createElement('label')
    label.htmlFor = checkbox.id
    label.append(checkbox)

    let span = document.createElement('span')
    span.textContent = option

    container.append(label)
    container.append(span)
    return container
  }

  search() {
    let query = this.inputTarget.value
    let queryRE = new RegExp(`^${query}.*`, 'i')
    let filteredOptions = this.options.filter((option) => queryRE.test(option))
    if (filteredOptions.length > 0) {
      this.renderOptions(filteredOptions)
    } else {
      this.typeaheadQuery = query
      this.loadFilterValues()
    }
  }
}
